import type { RefObject } from 'react';
import { useMemo } from 'react';
import { UsersIcon } from '@heroicons/react/24/outline';
import {
    DataTable,
    avatarColumn,
    type DataTableColumn,
    type DataTablePagination,
    type DataTableRefs,
    type DataTableSort,
    type SortOrder
} from '@odo/shared-ui';
import type { CatalogRole, IamUserListItem } from '../../../services/iam';
import { GrantChip } from '../primitives/GrantChip';
import { roleTitle } from '../iamLookups';
import { LastActivity } from '../../accounts/users/LastActivity';

export type AccessUsersSortBy = 'name' | 'activity';

const SKELETON_ROW_COUNT = 9;
const TD_PADDING = 'px-3 first:pl-5 last:pr-4 overflow-hidden';
const TH_PADDING = '!px-3 first:!pl-5 last:!pr-4';
const MAX_VISIBLE_GRANTS = 2;
const COL_WIDTHS = {
    user: '26%',
    grants: '38%',
    effective: '16%',
    lastActive: '20%'
};

function SkeletonCell({ width }: { width: string }) {
    return <div className={`h-3.5 rounded bg-gray-200 animate-pulse ${width}`} />;
}

function SkeletonRows() {
    const widths = ['w-44', 'w-40', 'w-16', 'w-24'];
    return (
        <>
            {Array.from({ length: SKELETON_ROW_COUNT }).map((_, i) => (
                <tr key={i} className="h-16">
                    {widths.map((width, j) => (
                        <td key={j} className={`${TD_PADDING} align-middle`}>
                            <SkeletonCell width={width} />
                        </td>
                    ))}
                </tr>
            ))}
        </>
    );
}

interface EmptyStateProps {
    search: string;
    hasFilters: boolean;
    onClearFilters: () => void;
}

function EmptyState({ search, hasFilters, onClearFilters }: EmptyStateProps) {
    return (
        <div className="flex flex-col items-center gap-3 text-center">
            <UsersIcon className="h-12 w-12 text-gray-300" strokeWidth={1} />
            <p className="text-sm text-gray-500">
                {search ? `No users match “${search}”.` : 'No users match these filters.'}
            </p>
            {hasFilters && (
                <button
                    type="button"
                    onClick={onClearFilters}
                    className="bg-white text-gray-800 border border-gray-300 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors font-semibold text-sm"
                >
                    Clear filters
                </button>
            )}
        </div>
    );
}

function GrantsCell({
    user,
    roleLookup
}: {
    user: IamUserListItem;
    roleLookup: ReadonlyMap<string, CatalogRole>;
}) {
    if (user.grants.length === 0) {
        return <span className="text-sm text-gray-300">No grants</span>;
    }
    const shown = user.grants.slice(0, MAX_VISIBLE_GRANTS);
    const rest = user.grants.length - shown.length;
    return (
        <div className="flex items-center gap-1.5 min-w-0 flex-wrap">
            {shown.map((grant, index) => (
                <GrantChip
                    key={`${grant.roleKey}-${grant.scope.type}-${grant.scope.id}-${index}`}
                    roleKey={grant.roleKey}
                    roleKind={grant.roleKind}
                    roleTitle={roleTitle(roleLookup.get(grant.roleKey))}
                    scope={grant.scope}
                    scopeLabel={grant.scopeLabel}
                />
            ))}
            {rest > 0 && (
                <span className="inline-flex items-center h-6 px-2 rounded-full bg-gray-100 text-[11px] font-bold text-gray-600">
                    +{rest}
                </span>
            )}
        </div>
    );
}

// Effective/unwired counts, computed server-side from the same grants -
// unwired (a permission with no route enforcing it yet) gets the "hole"
// colour the Routes page uses for its own 'open' status.
function EffectiveCell({ user }: { user: IamUserListItem }) {
    return (
        <div className="flex items-center gap-1.5 flex-wrap">
            <span className="inline-flex items-center h-6 px-2 rounded-full bg-gray-100 text-[11px] font-bold text-gray-600">
                {user.effectiveCount} effective
            </span>
            {user.unwiredCount > 0 && (
                <span className="inline-flex items-center h-6 px-2 rounded-full bg-rose-50 text-rose-700 text-[11px] font-bold">
                    {user.unwiredCount} unwired
                </span>
            )}
        </div>
    );
}

function buildColumns(
    roleLookup: ReadonlyMap<string, CatalogRole>
): DataTableColumn<IamUserListItem, AccessUsersSortBy>[] {
    return [
        avatarColumn<IamUserListItem, AccessUsersSortBy>({
            key: 'user',
            label: 'User',
            width: COL_WIDTHS.user,
            cellClassName: TD_PADDING,
            headerClassName: TH_PADDING,
            // Some rows (e.g. migration/system users) have no name - keep the
            // two-line shape: a small dash on the name line, email below as usual.
            name: (user) => user.name || '-',
            sublabel: (user) => user.email,
            avatarClassName: 'h-9 w-9',
            getTitle: (user) => (user.name ? `${user.name}\n${user.email}` : user.email)
        }),
        {
            key: 'grants',
            label: 'Grants',
            width: COL_WIDTHS.grants,
            sortable: false,
            cellClassName: TD_PADDING,
            headerClassName: TH_PADDING,
            render: (user) => <GrantsCell user={user} roleLookup={roleLookup} />
        },
        {
            key: 'effective',
            label: 'Access',
            width: COL_WIDTHS.effective,
            sortable: false,
            cellClassName: TD_PADDING,
            headerClassName: TH_PADDING,
            render: (user) => <EffectiveCell user={user} />
        },
        {
            key: 'lastActive',
            label: 'Last active',
            width: COL_WIDTHS.lastActive,
            sortable: true,
            sortKey: 'activity',
            cellClassName: TD_PADDING,
            headerClassName: `${TH_PADDING} whitespace-nowrap`,
            render: (user) => <LastActivity lastActivityAt={user.lastActive} />
        }
    ];
}

interface AccessUsersTableProps {
    users: IamUserListItem[];
    roleLookup: ReadonlyMap<string, CatalogRole>;
    isLoading: boolean;
    search: string;
    hasFilters: boolean;
    onClearFilters: () => void;
    onOpenUser: (user: IamUserListItem) => void;
    sortBy: AccessUsersSortBy;
    sortOrder: SortOrder;
    onSort: (column: AccessUsersSortBy) => void;
    page: number;
    totalPages: number;
    total: number;
    onPage: (page: number) => void;
    tableCardRef: RefObject<HTMLDivElement>;
    tableHeaderRef: RefObject<HTMLTableSectionElement>;
    tableBodyRef: RefObject<HTMLTableSectionElement>;
    paginationRef: RefObject<HTMLDivElement>;
}

export function AccessUsersTable({
    users,
    roleLookup,
    isLoading,
    search,
    hasFilters,
    onClearFilters,
    onOpenUser,
    sortBy,
    sortOrder,
    onSort,
    page,
    totalPages,
    total,
    onPage,
    tableCardRef,
    tableHeaderRef,
    tableBodyRef,
    paginationRef
}: AccessUsersTableProps) {
    const columns = useMemo(() => buildColumns(roleLookup), [roleLookup]);

    const sort: DataTableSort<AccessUsersSortBy> = { by: sortBy, order: sortOrder, onSort };
    const pagination: DataTablePagination = { page, totalPages, total, onPage };
    const refs: DataTableRefs = {
        card: tableCardRef,
        head: tableHeaderRef,
        body: tableBodyRef,
        pagination: paginationRef
    };

    return (
        <div className="flex-1 px-8 pb-8 overflow-hidden min-h-0 flex flex-col">
            <DataTable
                label="Users"
                columns={columns}
                rows={users}
                getRowKey={(user) => user.id}
                rowAction={{
                    onActivate: onOpenUser,
                    getLabel: (user) => `Open ${user.name}`
                }}
                rowHeightClassName="h-16"
                sort={sort}
                mode="paged"
                pagination={pagination}
                isLoading={isLoading}
                loadingRows={<SkeletonRows />}
                emptyState={
                    <EmptyState
                        search={search}
                        hasFilters={hasFilters}
                        onClearFilters={onClearFilters}
                    />
                }
                emptyStateCellClassName="px-3 py-16"
                refs={refs}
            />
        </div>
    );
}
